import React, { useState, useEffect } from "react";
import {
    Container,
    Row,
    Col,
    Spinner,
    Button,
    OverlayTrigger, Tooltip
} from "react-bootstrap";
import { useNavigate, useParams } from "react-router-dom";
import { ImDownload2 } from "react-icons/im";
import toast from "react-hot-toast";
import { Table } from "react-bootstrap";
import instance from "../../../services/baseService";
import Base from "../../Base";
import { FaArrowLeft } from 'react-icons/fa';
import { useSelector } from "react-redux";
import { BsInfoCircle } from "react-icons/bs";

function SinglePortCodeView() {
    const { client_id, report_id, port_code_id } = useParams();
    const [loading, setLoading] = useState(true);
    const [tableData, setTableData] = useState([]);
    const [reportData, setReportData] = useState({});
    const [portCodeData, setPortCodeData] = useState({
        gstNo: null,
        scripNo: null,
        scripDate: null,
        iecCode: null,
        portCode: null,
        total_count: 0,
        total_amount: 0
    });
    const navigate = useNavigate();
    const { client: singleClient } =
        useSelector((state) => state.clients);

    const handleBackToHome = () => {
        navigate(-1);
    };

    useEffect(() => {
        if (client_id && report_id && port_code_id) {
            fetchPortCodeData();
        }
    }, [client_id, report_id, port_code_id]);

    const fetchPortCodeData = async () => {
        try {
            setLoading(true);
            const response = await instance.post(
                `/api/v1/reconciliation/single/portcode/details/${client_id}/${report_id}`,
                {
                    port_code_id: parseInt(port_code_id),
                }
            );

            if (response.data.type === "success") {
                setReportData(response.data.data.uploaded_file);
                setPortCodeData({
                    gstNo: response.data.data.gstNo,
                    scripNo: response.data.data.scripNo,
                    scripDate: response.data.data.scrip_date,
                    iecCode: response.data.data.iec_code,
                    portCode: response.data.data.port_code,
                    total_count: response.data.data.total_count || 0,
                    total_amount: response.data.data.total_amount || 0
                });

                const records = response.data.data.lists || [];
                const mappedData = records.map((record) => ({
                    sbNo: record.sb_no || "",
                    sbDate: record.sb_date || "",
                    scrollNo: record.scroll_no || "N/A",
                    status: record.status || null,
                    type: record.amount_type || "",
                    amount: record.amount || "",
                    ifsc_code: record.ifsc_code || "",
                    brc_data: record.brc_data || []
                }));
                setTableData(mappedData);
            }
        } catch (error) {
            console.error("Error fetching port code data:", error);
            toast.error(error.response?.data?.message || "Failed to fetch data");
            setTableData([]);
        } finally {
            setLoading(false);
        }
    };

    const handleDownloadExcel = async () => {
        try {
            setLoading(true);
            const response = await instance.post(
                `/api/v1/reconciliation/port-code/excel/download/single/${client_id}/${report_id}/${port_code_id}`,
                {
                    include_brc_data: true
                },
                {
                    responseType: 'blob'
                }
            );

            // Extract filename from Content-Disposition header
            const contentDisposition = response.headers['content-disposition'];
            let filename = null;

            if (contentDisposition) {
                const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
                if (filenameMatch && filenameMatch[1]) {
                    filename = filenameMatch[1].replace(/['"]/g, '');
                }
            }

            if (!filename) {
                alert("Filename not found in headers, generating default filename.");
                const clientName = (singleClient?.client_name || "Client").replace(/[^a-zA-Z0-9_-]/g, '_');
                const scripNo = portCodeData?.scripNo || "Scrip";
                const scripDate = portCodeData?.scripDate
                    ? new Date(portCodeData.scripDate)
                        .toLocaleDateString("en-GB")
                        .replace(/\//g, '') // Convert dd/mm/yyyy to ddmmyyyy
                    : "NoDate";

                filename = `${clientName}_${scripNo}_${scripDate}.xlsx`;
            }

            const blob = new Blob([response.data], {
                type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
            });

            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', filename);
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);

            toast.success('Excel file downloaded successfully');
        } catch (error) {
            console.error("Download API Error:", error);
            toast.error(error.response?.data?.message || "Download failed");
        } finally {
            setLoading(false);
        }
    };

    const formatCurrency = (amount) => {
        if (!amount || amount === null || amount === undefined) return '₹0';
        const numAmount = typeof amount === 'string' ? parseFloat(amount) : amount;
        return `₹${numAmount.toLocaleString('en-IN')}`;
    };

    const formatISODate = (d) => {
        return new Date(d).toLocaleDateString("en-GB", { timeZone: "UTC" });
    };

    // =============================================================================
    // SB NUMBER EXPIRY HIGHLIGHTING LOGIC
    // Subsidy expires exactly 1 year from sb_date (handles leap years automatically)
    // - 0-180 days elapsed: Normal (no highlight)
    // - 181-299 days elapsed: Warning (light yellow)
    // - 300+ days elapsed (before expiry): Danger (light red)
    // - After expiry date: Expired (light red)
    // =============================================================================
    const WARNING_THRESHOLD = 181;  // Days after which warning starts
    const DANGER_THRESHOLD = 300;   // Days after which danger starts

    const getExpiryStatus = (sbDate) => {
        // Default safe return for invalid/missing dates
        const defaultReturn = {
            status: 'normal',
            daysElapsed: 0,
            daysRemaining: 365,
            expiryDate: null,
            sbDate: null
        };

        if (!sbDate) return defaultReturn;

        try {
            const today = new Date();
            today.setHours(0, 0, 0, 0);  // Reset time for accurate day calculation

            const sbDateObj = new Date(sbDate);

            // Validate parsed date
            if (isNaN(sbDateObj.getTime())) {
                console.warn('Invalid sb_date:', sbDate);
                return defaultReturn;
            }

            sbDateObj.setHours(0, 0, 0, 0);

            // =============================================================================
            // LEAP YEAR HANDLING:
            // Instead of using a fixed 365 days, we calculate the exact expiry date
            // by adding 1 year to sb_date. JavaScript Date automatically handles leap years.
            // Example: 
            //   - sb_date: 2024-02-29 → expiry: 2025-02-28 (leap year to non-leap year)
            //   - sb_date: 2023-03-01 → expiry: 2024-03-01 (includes Feb 29, 2024)
            // =============================================================================
            const expiryDate = new Date(sbDateObj);
            expiryDate.setFullYear(expiryDate.getFullYear() + 1);

            // Calculate days elapsed since sb_date
            const msPerDay = 1000 * 60 * 60 * 24;
            const daysElapsed = Math.floor((today - sbDateObj) / msPerDay);

            // Calculate days remaining until expiry (can be negative if expired)
            const daysRemaining = Math.floor((expiryDate - today) / msPerDay);

            // Determine status based on days elapsed and expiry
            let status = 'normal';
            if (daysRemaining <= 0) {
                // Already expired
                status = 'expired';
            } else if (daysElapsed >= DANGER_THRESHOLD) {
                // 300+ days elapsed, approaching expiry
                status = 'danger';
            } else if (daysElapsed >= WARNING_THRESHOLD) {
                // 181-299 days elapsed
                status = 'warning';
            }

            return {
                status,
                daysElapsed,
                daysRemaining: Math.max(0, daysRemaining),  // Ensure non-negative for display
                expiryDate,
                sbDate: sbDateObj
            };

        } catch (error) {
            // Catch any unexpected errors to prevent frontend crash
            console.error('Error calculating expiry status:', error);
            return defaultReturn;
        }
    };

    const getExpiryStyle = (status) => {
        switch (status) {
            case 'warning':
                return { backgroundColor: '#fff3cd', color: '#fd7e14' };  // Light yellow
            case 'danger':
                return { backgroundColor: '#f8d7da', color: '#dc3545' };  // Light red
            case 'expired':
                return { backgroundColor: '#f12536ff', color: '#ffffffff' };  // Dark red
            default:
                return {};
        }
    };

    const getExpiryTooltipText = (expiryInfo) => {
        const { status, daysRemaining, expiryDate } = expiryInfo;

        try {
            if (status === 'expired') {
                // Show the actual expiry date (1 year from sb_date)
                const expiryDateStr = expiryDate
                    ? expiryDate.toLocaleDateString("en-GB")
                    : 'unknown date';
                return `Subsidy expired on ${expiryDateStr}`;
            }

            return `Subsidy going to expire in ${daysRemaining} day${daysRemaining !== 1 ? 's' : ''}`;
        } catch (error) {
            console.error('Error generating tooltip text:', error);
            return 'Expiry information unavailable';
        }
    };

    // Reusable component for SB Number cell with expiry highlighting
    const SBNumberCell = ({ sbNo, sbDate, rowSpan }) => {
        const expiryInfo = getExpiryStatus(sbDate);
        const expiryStyle = getExpiryStyle(expiryInfo.status);
        const showIcon = expiryInfo.status !== 'normal';

        const cellStyle = {
            whiteSpace: 'nowrap',
            ...expiryStyle
        };

        const renderTooltip = (props) => (
            <Tooltip id={`tooltip-${sbNo}`} {...props} className="fw-bold">
                {getExpiryTooltipText(expiryInfo)}
            </Tooltip>
        );

        const cellContent = (
            <span className="d-flex align-items-center justify-content-between w-100 gap-2">
                {sbNo}
                {showIcon && (
                    <OverlayTrigger
                        placement="top"
                        delay={{ show: 100, hide: 200 }}
                        overlay={renderTooltip}
                    >
                        <span style={{ cursor: 'pointer', display: 'inline-flex' }}>
                            <BsInfoCircle size={14} />
                        </span>
                    </OverlayTrigger>
                )}
            </span>
        );

        if (rowSpan) {
            return (
                <td className="text-left" style={cellStyle} rowSpan={rowSpan}>
                    {cellContent}
                </td>
            );
        }

        return (
            <td className="text-left" style={cellStyle}>
                {cellContent}
            </td>
        );
    };

    const extractIFSC = (brcNo) => {
        if (!brcNo || brcNo.length < 11) return 'N/A';
        // First 4 (Bank) + 5th char (0) + Next 6 (Branch) = 11 characters
        return brcNo.substring(0, 11);
    };

    return (
        <Base>
            <Container fluid className="p-4 bg-light min-vh-100">
                <Row>
                    <div className="d-flex flex-column align-items-start mb-2">
                        <button
                            variant="link"
                            className="text-primary d-flex align-items-center gap-2 p-0 mb-2 transition-transform duration-200 hover:scale-105"
                            onClick={handleBackToHome}
                            style={{ fontSize: "16px", textDecoration: "none", background: "none", border: "none" }}
                        >
                            <FaArrowLeft size={14} /> Back
                        </button>
                    </div>
                    <Col>
                        <div className="bg-white p-4">
                            <div className="mb-3">
                                <h1 className="h4 fw-bold mb-2">Port Code Details</h1>
                                <p className="text-muted small">
                                    {reportData.start_date} - {reportData.end_date}
                                </p>
                            </div>

                            {loading ? (
                                <div className="text-center py-5">
                                    <Spinner animation="border" role="status">
                                        <span className="visually-hidden">Loading...</span>
                                    </Spinner>
                                </div>
                            ) : (
                                <>
                                    {/* Port Code Information Section */}
                                    <div className="mb-4 d-flex flex-wrap align-items-center gap-3 justify-content-between">
                                        <div>
                                            <strong className="me-2">Port Code:</strong>
                                            <span>{portCodeData.portCode || "N/A"}</span>
                                        </div>

                                        <div>
                                            <strong className="me-2">Scrip No:</strong>
                                            <span>{portCodeData.scripNo || "N/A"}</span>
                                        </div>
                                        <div>
                                            <strong className="me-2">Scrip Date:</strong>
                                            <span>
                                                {portCodeData.scripDate
                                                    ? formatISODate(portCodeData.scripDate)
                                                    : "N/A"}
                                            </span>
                                        </div>
                                        <div>
                                            <strong className="me-2">GST No:</strong>
                                            <span>{portCodeData.gstNo || "N/A"}</span>
                                        </div>

                                        <div>
                                            <strong className="me-2">IEC No:</strong>
                                            <span>{portCodeData.iecCode || "N/A"}</span>
                                        </div>

                                        <div>
                                            <strong className="me-2">Total Count:</strong>
                                            <span>{portCodeData.total_count || "N/A"}</span>
                                        </div>
                                        <div>
                                            <strong className="me-2">Total Amount:</strong>
                                            <span>{portCodeData.total_amount || "N/A"}</span>
                                        </div>
                                    </div>

                                    {/* Table Section */}
                                    <div
                                        className="overflow-auto"
                                        style={{ maxHeight: '500px', overflowY: 'auto' }}
                                    >
                                        <Table className="core-ui-table seperate-border">
                                            <thead
                                                className="table-light"
                                                style={{
                                                    position: 'sticky',
                                                    top: 0,
                                                    zIndex: 1,
                                                    backgroundColor: '#f8f9fa'
                                                }}
                                            >
                                                <tr>
                                                    <th style={{ whiteSpace: 'nowrap' }}>Sl No</th>
                                                    <th style={{ whiteSpace: 'nowrap' }}>SB NO</th>
                                                    <th style={{ whiteSpace: 'nowrap' }}>SB Date</th>
                                                    <th style={{ whiteSpace: 'nowrap' }}>Scroll No</th>
                                                    <th style={{ whiteSpace: 'nowrap' }}>Status</th>
                                                    <th style={{ whiteSpace: 'nowrap' }}>Type</th>
                                                    <th style={{ whiteSpace: 'nowrap' }}>Amount</th>
                                                    <th style={{ whiteSpace: 'nowrap' }}>FOB</th>
                                                    <th style={{ whiteSpace: 'nowrap' }}>BRC NO</th>
                                                    <th style={{ whiteSpace: 'nowrap' }}>BRC Date</th>
                                                    <th style={{ whiteSpace: 'nowrap' }}>
                                                        BRC Realised Value
                                                    </th>
                                                    <th style={{ whiteSpace: 'nowrap' }}>IFSC Code</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {tableData.length > 0 ? (
                                                    tableData.map((row, rowIndex) => {
                                                        const brcData = row.brc_data || [];
                                                        const brcCount = brcData.length || 1;

                                                        return (
                                                            <React.Fragment key={rowIndex}>
                                                                {brcCount === 0 || brcData.length === 0 ? (
                                                                    <tr>
                                                                        <td
                                                                            className="text-left"
                                                                            style={{ whiteSpace: 'nowrap' }}
                                                                        >
                                                                            {rowIndex + 1}
                                                                        </td>
                                                                        <SBNumberCell sbNo={row.sbNo} sbDate={row.sbDate} />
                                                                        <td
                                                                            className="text-left"
                                                                            style={{ whiteSpace: 'nowrap' }}
                                                                        >
                                                                            {row.sbDate}
                                                                        </td>
                                                                        <td
                                                                            className="text-left"
                                                                            style={{ whiteSpace: 'nowrap' }}
                                                                        >
                                                                            {row.scrollNo}
                                                                        </td>
                                                                        <td
                                                                            className="text-left"
                                                                            style={{ whiteSpace: 'nowrap' }}
                                                                        >
                                                                            {row.status}
                                                                        </td>
                                                                        <td
                                                                            className="text-left"
                                                                            style={{ whiteSpace: 'nowrap' }}
                                                                        >
                                                                            {row.type}
                                                                        </td>
                                                                        <td
                                                                            className="text-left"
                                                                            style={{ whiteSpace: 'nowrap' }}
                                                                        >
                                                                            {formatCurrency(row.amount)}
                                                                        </td>
                                                                        <td
                                                                            className="text-left text-muted"
                                                                            style={{ whiteSpace: 'nowrap' }}
                                                                        >
                                                                            N/A
                                                                        </td>
                                                                        <td
                                                                            className="text-left text-muted"
                                                                            style={{ whiteSpace: 'nowrap' }}
                                                                        >
                                                                            N/A
                                                                        </td>
                                                                        <td
                                                                            className="text-left text-muted"
                                                                            style={{ whiteSpace: 'nowrap' }}
                                                                        >
                                                                            N/A
                                                                        </td>
                                                                        <td
                                                                            className="text-left text-muted"
                                                                            style={{ whiteSpace: 'nowrap' }}
                                                                        >
                                                                            N/A
                                                                        </td>
                                                                        <td
                                                                            className="text-left text-muted"
                                                                            style={{ whiteSpace: 'nowrap' }}
                                                                        >
                                                                            N/A
                                                                        </td>
                                                                    </tr>
                                                                ) : (
                                                                    brcData.map((brc, brcIndex) => (
                                                                        <tr key={`${rowIndex}-${brcIndex}`}>
                                                                            {brcIndex === 0 && (
                                                                                <>
                                                                                    <td
                                                                                        className="text-left"
                                                                                        style={{
                                                                                            whiteSpace: 'nowrap'
                                                                                        }}
                                                                                        rowSpan={brcCount}
                                                                                    >
                                                                                        {rowIndex + 1}
                                                                                    </td>
                                                                                    <SBNumberCell sbNo={row.sbNo} sbDate={row.sbDate} rowSpan={brcCount} />
                                                                                    <td
                                                                                        className="text-left"
                                                                                        style={{
                                                                                            whiteSpace: 'nowrap'
                                                                                        }}
                                                                                        rowSpan={brcCount}
                                                                                    >
                                                                                        {row.sbDate}
                                                                                    </td>
                                                                                    <td
                                                                                        className="text-left"
                                                                                        style={{
                                                                                            whiteSpace: 'nowrap'
                                                                                        }}
                                                                                        rowSpan={brcCount}
                                                                                    >
                                                                                        {row.scrollNo}
                                                                                    </td>
                                                                                    <td
                                                                                        className="text-left"
                                                                                        style={{
                                                                                            whiteSpace: 'nowrap'
                                                                                        }}
                                                                                        rowSpan={brcCount}
                                                                                    >
                                                                                        {row.status}
                                                                                    </td>
                                                                                    <td
                                                                                        className="text-left"
                                                                                        style={{
                                                                                            whiteSpace: 'nowrap'
                                                                                        }}
                                                                                        rowSpan={brcCount}
                                                                                    >
                                                                                        {row.type}
                                                                                    </td>
                                                                                    <td
                                                                                        className="text-left"
                                                                                        style={{
                                                                                            whiteSpace: 'nowrap'
                                                                                        }}
                                                                                        rowSpan={brcCount}
                                                                                    >
                                                                                        {formatCurrency(row.amount)}
                                                                                    </td>
                                                                                </>
                                                                            )}
                                                                            <td
                                                                                className="text-left"
                                                                                style={{ whiteSpace: 'nowrap' }}
                                                                            >
                                                                                {formatCurrency(brc.fob)}
                                                                            </td>
                                                                            <td
                                                                                className="text-left"
                                                                                style={{ whiteSpace: 'nowrap' }}
                                                                            >
                                                                                {brc.brc_no || ''}
                                                                            </td>
                                                                            <td
                                                                                className="text-left"
                                                                                style={{ whiteSpace: 'nowrap' }}
                                                                            >
                                                                                {brc.brc_date || ''}
                                                                            </td>
                                                                            <td
                                                                                className="text-left"
                                                                                style={{ whiteSpace: 'nowrap' }}
                                                                            >
                                                                                {formatCurrency(
                                                                                    brc.brc_realised_value
                                                                                )}
                                                                            </td>
                                                                            <td className="text-left" style={{ whiteSpace: 'nowrap' }}>{extractIFSC(brc.brc_no)}</td>
                                                                        </tr>
                                                                    ))
                                                                )}
                                                            </React.Fragment>
                                                        );
                                                    })
                                                ) : (
                                                    <tr>
                                                        <td colSpan={12} className="text-center py-4">
                                                            No data available
                                                        </td>
                                                    </tr>
                                                )}
                                            </tbody>
                                        </Table>
                                    </div>

                                    {/* Download Button Section */}
                                    <div className="d-flex justify-content-end mt-4">
                                        <Button
                                            variant="primary"
                                            className="fw-bold px-4 shadow-sm d-flex align-items-center gap-1"
                                            onClick={handleDownloadExcel}
                                            disabled={loading}
                                        >
                                            <ImDownload2 />
                                            Download Excel
                                        </Button>
                                    </div>
                                </>
                            )}
                        </div>
                    </Col>
                </Row>
            </Container>
        </Base>
    );
}

export default SinglePortCodeView;